import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const mockPricingPlan = {
  name: 'Premium',
  price: 19,
  pagesPerMonth: 300,
  features: [
    'Convert 300 pages per month',
    'Support for 1000+ banks worldwide',
    'Export to Excel (XLS) and CSV',
    'Priority email support',
    'Secure encrypted processing',
    'Bulk conversion support',
    'API access'
  ]
};

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      console.log('Creating checkout session...');
      const result = await subscriptionAPI.createCheckoutSession();
      console.log('Checkout session created:', result);
      
      if (result && result.checkoutUrl) {
        toast({
          title: 'Redirecting to checkout...',
          description: 'Please wait while we prepare your subscription.'
        });
        // Small delay to show the toast
        setTimeout(() => {
          window.location.href = result.checkoutUrl;
        }, 500);
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.message || 'Failed to create checkout session. Please try again.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-pink-100 to-pink-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Choose the plan that works best for your needs. No hidden fees, cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Tier */}
          <Card className="border-pink-200 hover:shadow-xl transition-all bg-white/60 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Free</CardTitle>
              <CardDescription className="text-gray-700">For trying out our service</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-pink-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">1 page conversion per day</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-pink-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">No registration required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-pink-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Basic support</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-pink-200 hover:border-pink-300 hover:bg-pink-50"
              >
                Get Started
              </Button>
            </CardFooter>
          </Card>

          {/* Premium Tier - Highlighted */}
          <Card className="border-pink-400 hover:shadow-2xl transition-all relative bg-gradient-to-br from-pink-50 to-white/80 backdrop-blur-sm">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-pink-500 to-pink-400 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                Most Popular
              </span>
            </div>
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Premium</CardTitle>
              <CardDescription className="text-gray-700">For professionals and businesses</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">${mockPricingPlan.price}</span>
                <span className="text-gray-600">/month</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {mockPricingPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-pink-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-400 to-pink-300 hover:from-pink-500 hover:to-pink-400 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Subscribe Now'}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-sm p-6 rounded-lg border border-pink-200 shadow-md">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                What file formats do you support?
              </h3>
              <p className="text-gray-700">
                We support PDF bank statements from over 1000 banks worldwide. Output formats include Excel (XLS, XLSX) and CSV.
              </p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm p-6 rounded-lg border border-pink-200 shadow-md">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Is my data secure?
              </h3>
              <p className="text-gray-700">
                Yes! We use bank-grade encryption and delete all files after 24 hours. We never store or share your financial data.
              </p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm p-6 rounded-lg border border-pink-200 shadow-md">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Can I cancel my subscription anytime?
              </h3>
              <p className="text-gray-700">
                Absolutely! You can cancel your subscription at any time with no questions asked. No cancellation fees.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;